import {
  Box,
  Button,
  Flex,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import ErrorState from '../../../components/states/ErrorState';
import { useMemo, useState, ChangeEvent } from 'react';
import { translate, TranslationKeys } from '../../../i18n';
import { ChurnTier, UserChurnScoreDto } from '../../../models/churn-prediction/dto/churn-prediction.dto';
import {
  tableCellProps,
  tableHeaderTextProps,
  tableProps,
  tableRowProps,
  tableThFlexProps,
  tableThProps,
} from '../../../theme/constants';
import { typography } from 'theme/tokens';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetChurnUsers } from '../hooks/useGetChurnUsers';
import { TIER_LABEL_KEYS } from '../utils/churn.utils';
import ChurnTierBadge from './ChurnTierBadge';
import ChurnUserSignalModal from './ChurnUserSignalModal';
import { matchesNameQuery } from '../../../utils/name-search';

const columnHelper = createColumnHelper<UserChurnScoreDto>();
const PAGE_SIZE = 10;

export default function ChurnUsersTable() {
  const { selectedOrganization } = useSelectedOrganization();
  const [tierFilter, setTierFilter] = useState<ChurnTier | undefined>(undefined);
  const [searchText, setSearchText] = useState('');
  const [offset, setOffset] = useState(0);
  const [selectedUser, setSelectedUser] = useState<UserChurnScoreDto | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data, isLoading, isInitialLoading, isError } = useGetChurnUsers(
    selectedOrganization?.id,
    tierFilter,
    PAGE_SIZE,
    offset,
  );
  // `enabled: Boolean(organizationId)`, so React Query pins `isLoading: true`
  // whenever no organization is selected (fetchStatus 'idle'). This table had
  // no scope guard, so that raw flag drove the "Loading…" text FOREVER — the
  // permanent-spinner defect run 103 fixed on six list pages. `classifyRead`
  // keys off the query's own state instead of a re-derived gate (D62).
  const readState = classifyRead(isError, isInitialLoading, data);

  const users = data?.users || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const filteredUsers = useMemo(() => {
    if (!searchText) return users;
    return users.filter((u) => matchesNameQuery(searchText, u));
  }, [users, searchText]);

  const handleTierChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setTierFilter(value ? (value as ChurnTier) : undefined);
    setOffset(0);
  };

  const handleRowClick = (user: UserChurnScoreDto) => {
    setSelectedUser(user);
    onOpen();
  };

  const columns = useMemo(() => [
    columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
      id: 'name',
      header: () => (
        <Text {...tableHeaderTextProps}>{translate('churn_prediction.name')}</Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('email', {
      id: 'email',
      header: () => (
        <Text {...tableHeaderTextProps}>{translate('churn_prediction.email')}</Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('score', {
      id: 'score',
      header: () => (
        <Text {...tableHeaderTextProps}>{translate('churn_prediction.score')}</Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="600">
          {(info.getValue() * 100).toFixed(0)}%
        </Text>
      ),
    }),
    columnHelper.accessor('tier', {
      id: 'tier',
      header: () => (
        <Text {...tableHeaderTextProps}>{translate('churn_prediction.tier')}</Text>
      ),
      cell: (info) => <ChurnTierBadge tier={info.getValue()} />,
    }),
    columnHelper.accessor('calculatedAt', {
      id: 'calculatedAt',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('churn_prediction.last_calculated')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {new Date(info.getValue()).toLocaleDateString()}
        </Text>
      ),
    }),
  ], [textColor]);

  const table = useReactTable({
    data: filteredUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (readState === 'never-issued') {
    return (
      <Card flexDirection="column" w="100%" px="0px">
        <ScopePrompt gate="organization" />
      </Card>
    );
  }

  return (
    <>
      <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
          <Text color={textColor} {...typography.pageTitle}>
            {translate('churn_prediction.users')}
          </Text>
        </Flex>

        <Flex px="25px" mb="16px" gap="12px" flexWrap="wrap">
          <Select
            placeholder={translate('churn_prediction.all_tiers')}
            value={tierFilter || ''}
            onChange={handleTierChange}
            maxW="200px"
            size="sm"
            borderRadius="8px"
          >
            {Object.values(ChurnTier).map((tier) => (
              <option key={tier} value={tier}>
                {translate(TIER_LABEL_KEYS[tier] as TranslationKeys)}
              </option>
            ))}
          </Select>
          <Input
            placeholder={translate('churn_prediction.search_placeholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            maxW="300px"
            size="sm"
            borderRadius="8px"
          />
        </Flex>

        {isLoading ? (
          <Flex px="25px" py="50px" justifyContent="center">
            <Text color="gray.500">{translate('churn_prediction.loading')}</Text>
          </Flex>
        ) : readState === 'unreadable' ? (
          // `unreadable`, not raw `isError`: a background refetch that fails
          // while good rows are on screen keeps them (R96-3). `isUnreadable`'s
          // `!data` half is what draws that line.
          <ErrorState message={translate('churn_prediction.error_loading')} />
        ) : filteredUsers.length === 0 ? (
          <Flex px="25px" py="50px" justifyContent="center">
            <Text color="gray.500">{translate('churn_prediction.no_users')}</Text>
          </Flex>
        ) : (
          <Box>
            <Table {...tableProps}>
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th key={header.id} colSpan={header.colSpan} {...tableThProps} borderColor={borderColor}>
                        <Flex {...tableThFlexProps}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map((row) => (
                  <Tr
                    key={row.id}
                    {...tableRowProps}
                    onClick={() => handleRowClick(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id} {...tableCellProps}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <Flex px="25px" pb="20px" justify="space-between" align="center">
              <Button
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                isDisabled={offset === 0}
              >
                {translate('churn_prediction.previous')}
              </Button>
              <Text fontSize="sm" color="gray.500">
                {translate('churn_prediction.page')} {currentPage}{' '}
                {translate('churn_prediction.of')} {totalPages || 1}
              </Text>
              <Button
                size="sm"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                isDisabled={offset + PAGE_SIZE >= total}
              >
                {translate('churn_prediction.next')}
              </Button>
            </Flex>
          </Box>
        )}
      </Card>

      <ChurnUserSignalModal isOpen={isOpen} onClose={onClose} user={selectedUser} />
    </>
  );
}
